import React, { useEffect, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import Loader from "../common/Loader.jsx";
import { sanitizeInput } from "../common/security-utils";
import { getCustomModuleApi, getRecordApi, saveRecordApi, uploadRecordFileApi } from "./custom-module-api";
import { defaultsOf, firstErrors, isShown, singularOf } from "./custom-module-utils";
import { RecordForm } from "./record-fields";

// Create and edit share this page: /custom/:slug/create and /custom/:slug/edit/:id.
const CustomRecordCreate = () => {
  const navigate = useNavigate();
  const { slug, id } = useParams();

  const [module, setModule] = useState(null);
  const [record, setRecord] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", success: true });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = id ? await getRecordApi(slug, id) : await getCustomModuleApi(slug);
        if (response?.data?.success) {
          const loaded = response.data.module;
          setModule(loaded);
          setRecord(id ? response.data.data : null);
          setValues(id ? response.data.data?.data ?? {} : defaultsOf(loaded));
        }
      } catch (error) {
        console.error("Error loading form:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, id]);

  const showToast = (message, success) => setToast({ show: true, message, success });

  const handleSubmit = async () => {
    setSaving(true);
    setErrorMsg({});
    try {
      const clean = (value) => (typeof value === "string" ? sanitizeInput(value) : value ?? null);
      // Hidden fields are not form input; the server keeps their stored values.
      const data = Object.fromEntries(
        module.fields.filter(isShown).map(({ key, type }) => {
          const value = values[key];
          if (type === "repeater" && Array.isArray(value)) {
            return [key, value.map((row) => Object.fromEntries(Object.entries(row ?? {}).map(([cell, text]) => [cell, clean(text)])))];
          }
          return [key, clean(value)];
        })
      );
      const response = await saveRecordApi(slug, id, { data });

      if (response?.status === 400) {
        setErrorMsg(firstErrors(response.data?.error));
        showToast(response.data?.message ?? "Validation error", false);
        return;
      }
      showToast(response?.data?.message ?? "Saved", response?.data?.success);
      if (response?.data?.success) setTimeout(() => navigate(`/custom/${slug}/view/${response.data.id}`), 600);
    } catch (error) {
      console.error("Error saving record:", error);
      showToast("Unable to save record", false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader height="80vh" />;
  if (!module) {
    return (
      <div className="fs-14 fw-500 text-grey text-center" style={{ padding: "80px 0" }}>
        This record or module does not exist.
      </div>
    );
  }

  return (
    <div style={{ padding: "16px" }}>
      <ToastContainer position="top-end">
        <Toast bg={toast.success ? "success" : "danger"} show={toast.show} onClose={() => setToast({ ...toast, show: false })} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="mb-3">
        <div className="fs-20 fw-600 text-dkblue">
          {id ? "Edit" : "Create"} {singularOf(module)}
        </div>
        {record?.code && <div className="fs-13 fw-500 text-grey">{record.code}</div>}
      </div>

      <RecordForm
        module={module}
        values={values}
        errors={errorMsg}
        upload={(field, name, content) => uploadRecordFileApi(slug, field, name, content)}
        onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
      />

      <div className="d-flex justify-content-end gap-2">
        <button className="btn-cancel" onClick={() => navigate(-1)}>Back</button>
        <button className="btn-save" disabled={saving} onClick={handleSubmit}>
          {saving ? "Saving..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default CustomRecordCreate;
